import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, fmtDate, ageDays } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Money, Empty, Kpi } from "@/components/common";
import { ClassifyForm } from "@/components/ClassifyForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Suspense() {
  const { accounts, users, can } = useApp();
  const [items, setItems] = useState([]);
  const [resolve, setResolve] = useState(null);
  const load = () => api.get("/transactions", { params: { status: "suspense", sort: "txn_date", order: 1, limit: 2000 } }).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const assign = async (id, reviewer) => {
    try { await api.post(`/transactions/${id}/assign`, { reviewer }); toast.success("Reviewer assigned"); load(); } catch (e) { toast.error(await errMsg(e)); }
  };
  const bucket = (d) => (d <= 7 ? "0–7 days" : d <= 30 ? "8–30 days" : "30+ days");
  const balance = items.reduce((a, t) => a + Number(t.amount) * (t.direction === "credit" ? 1 : -1), 0);
  const groups = ["0–7 days", "8–30 days", "30+ days"].map((b) => [b, items.filter((t) => bucket(ageDays(t.suspense?.since)) === b)]);
  return (
    <div>
      <PageHeader title="Suspense Account" sub="Entries that could not be classified confidently. Each must be resolved with a note by a person — nothing is cleared silently." />
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Kpi label="Open entries" value={items.length} testid="suspense-open-count" />
        <Kpi label="Net suspense balance" value={inr(balance.toFixed(2))} tone="rose" testid="suspense-net-balance" />
        {groups.slice(1).map(([b, l]) => <Kpi key={b} label={b} value={l.length} tone="amber" />)}
      </div>
      {items.length === 0 && <Empty title="Suspense is clear" sub="No pending entries." />}
      {groups.map(([b, list]) => list.length > 0 && (
        <div key={b} className="mb-6">
          <h3 className="mb-2 text-sm font-semibold">{b}</h3>
          <div className="panel overflow-x-auto">
            <table className="w-full min-w-[1000px] text-xs">
              <thead className="text-left text-muted-foreground"><tr>{["Date", "Amount", "Narration", "Bank", "Suggested party", "Missing info", "Reviewer", "Age", "Status", ""].map((h) => <th key={h} className="p-2.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>{list.map((t) => {
                const a = accounts.find((x) => x.id === t.account_id);
                return (
                  <tr key={t.id} className="border-t" data-testid="suspense-row">
                    <td className="num p-2.5">{fmtDate(t.txn_date)}</td><td className="p-2.5"><Money v={t.amount} dir={t.direction} /></td>
                    <td className="max-w-[260px] truncate p-2.5 font-mono" title={t.narration}>{t.narration}</td>
                    <td className="p-2.5">{a?.bank_name} {a?.masked}</td>
                    <td className="p-2.5">{t.party_extracted || t.ai_suggestion?.party || "—"}</td>
                    <td className="p-2.5 text-amber-600 dark:text-amber-400">{(t.suspense?.missing || []).join(", ") || "—"}</td>
                    <td className="p-2.5">
                      <Select value={t.suspense?.reviewer || ""} onValueChange={(v) => assign(t.id, v)} disabled={!can("admin", "accountant")}>
                        <SelectTrigger className="h-8 w-40" data-testid="suspense-reviewer-select"><SelectValue placeholder="Assign" /></SelectTrigger>
                        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="num p-2.5">{ageDays(t.suspense?.since)}d</td>
                    <td className="p-2.5">{t.suspense?.resolution || "open"}</td>
                    <td className="p-2.5"><Button size="sm" onClick={() => setResolve(t)} disabled={!can("admin", "accountant")} data-testid="suspense-resolve-button">Resolve</Button></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      ))}
      <Dialog open={!!resolve} onOpenChange={(o) => !o && setResolve(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Resolve suspense entry · {inr(resolve?.amount)}</DialogTitle></DialogHeader>
          {resolve && <><div className="rounded bg-muted p-2 font-mono text-xs">{resolve.narration}</div>
            <ClassifyForm txns={[resolve]} mode="suspense" submitLabel="Resolve & classify" onDone={() => { setResolve(null); load(); }} /></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
